import { ReactNode } from 'react';
import { Box, Card, CardActionArea, Stack, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useNavigate } from 'react-router-dom';

export type IconComponent = typeof TrendingUpIcon;

interface KpiCardProps {
  label: string;
  value: number | string;
  icon: IconComponent;
  color: string;
  to?: string;
  /** Only supply this when there's a real day-over-day figure behind it — no invented trends. */
  trendPercent?: number | null;
  /** The CRM Dashboard's compact tile (2026-11-03): ~108px tall, 32px icon,
   * 28px figure, 12.5px label. Other pages keep the original size. */
  compact?: boolean;
}

const Trend = ({ percent, compact }: { percent: number; compact: boolean }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={0.25}
    aria-label={`${percent >= 0 ? '+' : ''}${percent}% vs yesterday`}
    title="vs yesterday"
    sx={{ color: percent >= 0 ? 'success.main' : 'error.main', whiteSpace: 'nowrap', minWidth: 0 }}
  >
    {percent >= 0 ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : <TrendingDownIcon sx={{ fontSize: 16 }} />}
    <Typography variant="caption" sx={{ fontWeight: 700 }}>
      {percent >= 0 ? '+' : ''}
      {percent}%
    </Typography>
    {compact && (
      // Dropped on a narrow tile (two-up on a phone) — the tooltip still says it.
      <Typography variant="caption" color="text.secondary" className="kpi-trend-suffix" sx={{ pl: 0.25 }}>
        vs yesterday
      </Typography>
    )}
  </Stack>
);

export const KpiCard = ({ label, value, icon: Icon, color, to, trendPercent, compact = false }: KpiCardProps) => {
  const navigate = useNavigate();
  const hasTrend = trendPercent !== undefined && trendPercent !== null;

  const content = compact ? (
    // Figure and icon on one row, label beneath (≤ 2 lines): every tile's
    // figure sits at the same height, and a wrapped label still fits the tile.
    <Stack sx={{ px: 1.75, py: 1.5, height: '100%', boxSizing: 'border-box' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="div"
            sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}
          >
            {value}
          </Typography>
          {hasTrend && <Trend percent={trendPercent} compact />}
        </Box>
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}1a`,
            color,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
      </Stack>
      <Typography
        color="text.secondary"
        sx={{
          fontSize: 12.5,
          lineHeight: 1.3,
          mt: 'auto',
          pt: 0.75,
          // Two lines at most, so a long label never pushes the tile taller.
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {label}
      </Typography>
    </Stack>
  ) : (
    <Stack spacing={1.5} sx={{ p: 2.25 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}1a`,
            color,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        {hasTrend && <Trend percent={trendPercent} compact={false} />}
      </Stack>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      {hasTrend && (
        <Typography variant="caption" color="text.secondary">
          vs yesterday
        </Typography>
      )}
    </Stack>
  );

  return (
    <Card
      sx={{
        height: '100%',
        ...(compact
          ? {
              minHeight: 108,
              containerType: 'inline-size',
              '@container (max-width: 200px)': { '& .kpi-trend-suffix': { display: 'none' } },
            }
          : {}),
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': to ? { transform: 'translateY(-3px)', boxShadow: 6 } : undefined,
      }}
    >
      {to ? (
        <CardActionArea onClick={() => navigate(to)} sx={compact ? { height: '100%' } : undefined}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
};

/** The compact KPI tiles' grid (2026-11-03): 4 across on desktop, 2 on a
 * tablet, 2 on a phone with room for them (≥ 400px) and 1 below that;
 * 12px gaps, and every row the same height so tiles line up even when a
 * label wraps. */
export const KpiGrid = ({ children }: { children: ReactNode }) => (
  <Box
    data-testid="kpi-grid"
    sx={(theme) => ({
      display: 'grid',
      gap: 1.5,
      gridAutoRows: '1fr',
      mb: 1,
      gridTemplateColumns: {
        xs: 'minmax(0, 1fr)',
        sm: 'repeat(2, minmax(0, 1fr))',
        md: 'repeat(4, minmax(0, 1fr))',
      },
      // A phone with room for two (400–599px) gets two; narrower ones, one.
      [`@media (min-width: 400px) and (max-width: ${theme.breakpoints.values.sm - 0.05}px)`]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
    })}
  >
    {children}
  </Box>
);
